/**
 * Utility functions for handling and formatting API errors
 */

export interface ApiError {
  response?: {
    status?: number;
    data?: {
      detail?: string;
      message?: string;
    };
  };
  message?: string;
}

/**
 * Extract user-friendly error message from API error
 */
export function extractErrorMessage(error: any): string {
  // Handle different error formats
  if (error?.response?.data?.detail) {
    return error.response.data.detail;
  }
  
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error?.message) {
    // For network errors or other client-side errors
    if (error.message.includes('Network Error') || error.message.includes('timeout')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    
    if (error.message.includes('401')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    
    if (error.message.includes('429')) {
      return 'Too many login attempts. Please wait a few minutes before trying again.';
    }
    
    if (error.message.includes('500')) {
      return 'An unexpected error occurred. Please try again later or contact support if the problem persists.';
    }
    
    return error.message;
  }
  
  // Default fallback message
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Check if error is an authentication error
 */
export function isAuthError(error: any): boolean {
  const status = error?.response?.status;
  return status === 401 || status === 403;
}

/**
 * Check if error is a rate limiting error
 */
export function isRateLimitError(error: any): boolean {
  return error?.response?.status === 429;
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: any): boolean {
  return !error?.response && (error?.message?.includes('Network Error') || error?.message?.includes('timeout'));
}
